import React, { useState, useEffect } from "react";
import { Card, Row, Col, Typography, Space, Spin } from "antd";
import {
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import "./OfficeInfo.css";
import officeBuilding from "../assets/icons/corporate_fare.svg";
import axiosInstance from "../services/axios";
import { useTranslation } from "react-i18next";
import EmptyState from "./EmptyState";

const { Title, Paragraph, Text } = Typography;

export default function OfficeInfo() {
  const { i18n } = useTranslation();
  
  const [officeData, setOfficeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchOfficeInfo = async () => {
      setLoading(true);
      try {
        const resp = await axiosInstance.get('/office-address/');

        if (!mounted) return;

        let data = resp.data;
        if (Array.isArray(data)) {
          data = data[0];
        } else if (data && typeof data === 'object') {
          if (data.results && Array.isArray(data.results)) {
            data = data.results[0];
          } else if (data.data && Array.isArray(data.data)) {
            data = data.data[0];
          }
        }

        if (data) {
          setOfficeData({
            title: data.title || "कार्यालयाची माहिती",
            imageUrl: data.image,
            officeName: data.office_name || "ग्राम पंचायत कार्यालय",
            addressLine1: data.address_line1,
            addressLine2: data.address_line2,
            phones: data.phone_number ? [data.phone_number] : [],
            email: data.email,
            hours: data.office_hours ? [data.office_hours] : [],
          });
        } else {
          setOfficeData(null);
        }
      } catch (err) {
        console.error('Failed to load office info:', err);
        setOfficeData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOfficeInfo();
    return () => { mounted = false; };
  }, [i18n.language]);

  if (!officeData) {
    return (
      <div id="address" className="office-info-wrap section-padding">
        <EmptyState description={i18n.language === 'mr' ? 'कार्यालयाची माहिती उपलब्ध नाही' : 'Office information not available'} />
      </div>
    );
  }

  const {
    title = "कार्यालयाची माहिती",
    imageUrl,
    officeName = "ग्राम पंचायत कार्यालय",
    addressLine1,
    addressLine2,
    phones = [],
    email,
    hours = [],
  } = officeData;

  // Only use image if it's a full URL from API
  const officeImage = imageUrl && (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) ? imageUrl : null;

  if (loading) {
    return (
      <div id="address" className="office-info-wrap section-padding">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div id="address" className="office-info-wrap section-padding">
      <Card className="office-card" bordered>
        <Title level={2} className="office-header">
          <img loading="lazy" src={officeBuilding} alt="office" />
          {title}
        </Title>

        <Row gutter={[24, 16]} className="office-row">
          {/* Left Image - Only show if imageUrl exists */}
          {officeImage && (
            <Col xs={24} md={12} className="office-left">
              <div className="office-image-wrapper">
                <img
                  src={officeImage}
                  alt={officeName}
                  className="office-image"
                />
              </div>
            </Col>
          )}

          {/* Right Details */}
          <Col xs={24} md={officeImage ? 12 : 24} className="office-right">
            <div className="office-details">
              <div className="office-details-inner">
                <Space direction="vertical" size="large">
                  <div className="office-detail-row">
                    <EnvironmentOutlined className="detail-icon" />
                    <div>
                      <Paragraph strong className="address-text mb-0">
                        {addressLine1}<br />
                        {addressLine2}
                      </Paragraph>
                    </div>
                  </div> 

                  <div className="office-detail-row">
                    <PhoneOutlined className="detail-icon" />
                    <div>
                      {phones.map((p, i) => (
                        <div key={i}><Text strong>{p}</Text></div>
                      ))}
                    </div>
                  </div>

                  <div className="office-detail-row">
                    <MailOutlined className="detail-icon" />
                    <Text strong>{email}</Text>
                  </div>

                  <div className="office-detail-row">
                    <ClockCircleOutlined className="detail-icon" />
                    <div>
                      <Text strong>Office Hours:</Text>
                      {hours.map((h, i) => (
                        <div key={i}><Text strong>{h}</Text></div>
                      ))}
                    </div>
                  </div>
                </Space>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
